import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';

interface BookStatusData {
  bookId: string;
  status: 'queued' | 'generating' | 'editing' | 'complete' | 'failed';
  currentStage: string;
  startedAt: string;
  updatedAt: string;
  completedAt?: string;
  failureReason?: string;
  revisionIteration?: number;
}

const STAGE_LABELS: Record<string, string> = {
  'brief-to-plot': 'Building plot structure',
  'plot-to-bible': 'Creating story bible',
  'bible-to-outline': 'Outlining chapters',
  'outline-to-scenes': 'Breaking into scenes',
  'prose-per-scene': 'Writing prose',
  'continuity-check': 'Checking continuity',
  'quality-scoring': 'Scoring quality',
  'output-assembly': 'Assembling manuscript',
};

const STAGE_ORDER = [
  'brief-to-plot',
  'plot-to-bible',
  'bible-to-outline',
  'outline-to-scenes',
  'prose-per-scene',
  'continuity-check',
  'quality-scoring',
  'output-assembly',
];

export default function GenerationStatus() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<BookStatusData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const poll = async () => {
      try {
        const res = await fetch(`/api/book/${id}/status`);
        if (!res.ok) {
          setError('Failed to load status');
          return;
        }
        const data: BookStatusData = await res.json();
        setStatus(data);
        if (data.status === 'complete' || data.status === 'failed') {
          if (pollRef.current) clearInterval(pollRef.current);
        }
      } catch {
        setError('Connection error');
      }
    };

    poll();
    pollRef.current = setInterval(poll, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [id]);

  const currentStageIndex = status ? STAGE_ORDER.indexOf(status.currentStage) : -1;

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between px-5 sm:px-8 py-3.5 border-b border-border">
        <a href="/" className="font-display text-[17px] text-foreground flex items-center gap-2">
          <span className="w-[7px] h-[7px] rounded-full bg-primary inline-block" aria-hidden="true" />
          Ghostwright
        </a>
      </header>

      <main className="max-w-[560px] mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <div className="text-[10px] tracking-[0.14em] uppercase text-placeholder mb-1">
          <span className="text-primary/60" aria-hidden="true">§</span> Generating
        </div>
        <h1 className="font-display text-[28px] font-normal italic text-heading mb-2 [font-variation-settings:'opsz'_96,'SOFT'_60]">
          Your book is being pressed
        </h1>
        <p className="text-sm text-muted-foreground mb-10">
          This typically takes 30-60 minutes. We will email you when it is ready.
        </p>

        {error && <p className="text-sm text-destructive mb-4">{error}</p>}

        {status && status.status === 'failed' && (
          <div className="p-4 bg-surface border border-destructive/30 rounded-lg mb-8">
            <p className="text-sm font-medium text-destructive mb-1">Generation failed</p>
            <p className="text-xs text-muted-foreground">{status.failureReason ?? 'An unexpected error occurred.'}</p>
          </div>
        )}

        {status && status.status === 'complete' && (
          <div className="p-4 bg-surface border border-success/30 rounded-lg mb-8">
            <p className="text-sm font-medium text-success mb-2">Your book is ready</p>
            <button
              onClick={() => navigate(`/book/${status.bookId}`)}
              className="text-xs font-medium text-primary cursor-pointer border-none bg-transparent"
            >
              View your book
            </button>
          </div>
        )}

        <section>
          <h2 className="text-sm font-semibold text-foreground mb-4">Pipeline Progress</h2>
          <div className="space-y-0">
            {STAGE_ORDER.map((stage, i) => {
              let stageStatus: 'done' | 'active' | 'pending' = 'pending';
              if (i < currentStageIndex) stageStatus = 'done';
              else if (i === currentStageIndex) stageStatus = 'active';

              return (
                <div key={stage} className="flex items-center gap-3 py-2.5">
                  <StageIndicator status={stageStatus} />
                  <span className={`text-sm ${stageStatus === 'active' ? 'text-foreground font-medium' : stageStatus === 'done' ? 'text-muted-foreground' : 'text-placeholder'}`}>
                    {STAGE_LABELS[stage] ?? stage}
                  </span>
                  {stageStatus === 'active' && status?.revisionIteration && status.revisionIteration > 0 && stage === 'quality-scoring' && (
                    <span className="text-[10px] text-primary ml-auto">
                      Revision {status.revisionIteration}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
}

function StageIndicator({ status }: { status: 'done' | 'active' | 'pending' }) {
  if (status === 'done') {
    return <span className="w-3 h-3 rounded-full bg-success flex-shrink-0" />;
  }
  if (status === 'active') {
    return <span className="w-3 h-3 rounded-full bg-primary animate-pulse flex-shrink-0" />;
  }
  return <span className="w-3 h-3 rounded-full border-[1.5px] border-border flex-shrink-0" />;
}
